import React from 'react';

const Section = ({ n, title, children }) => (
  <section className="mb-8" id={`s-${n}`}>
    <h2 className="font-comfortaa font-bold text-xl sm:text-2xl text-ink mt-8 mb-3">
      <span className="text-lime mr-2">{n}.</span>{title}
    </h2>
    <div className="space-y-3 text-ink-700 text-sm sm:text-base font-light leading-relaxed">
      {children}
    </div>
  </section>
);

const Terms = () => {
  const today = new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="bg-white min-h-screen font-mont">
      <header className="bg-black text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-comfortaa font-bold tracking-[0.25em] text-lime">LEGAL</div>
          <h1 className="mt-3 font-comfortaa font-bold text-3xl sm:text-5xl">Términos y Condiciones</h1>
          <p className="mt-3 text-white/60 font-light text-sm">
            Última actualización: {today} · Versión 1.0 · Borrador para revisión legal
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="terms-page">
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-8 text-sm text-amber-900 font-light">
          ⚠️ <strong className="font-medium">Borrador.</strong> Este documento es un draft inicial preparado por Mobility para
          revisión por asesoría legal externa antes de su publicación definitiva. Debe ser validado por un abogado autorizado
          en México de acuerdo con la LFPDPPP, LFPC y, en lo aplicable, la Ley para Regular las Instituciones de Tecnología Financiera.
        </div>

        <p className="text-ink-700 text-sm sm:text-base font-light leading-relaxed">
          Bienvenido a <strong className="font-medium">Mobility</strong> ("la Plataforma"), operada por <em>Mobility Car Rental, S.A. de C.V.</em>
          (en lo sucesivo "Mobility", "nosotros" o "la Empresa"), con domicilio en México. Al acceder, registrarte o utilizar la
          Plataforma, aceptas estos Términos y Condiciones (los "Términos"). Si no estás de acuerdo, no utilices la Plataforma.
        </p>

        <Section n="1" title="Naturaleza del servicio: marketplace neutral P2P">
          <p>
            Mobility opera exclusivamente como un <strong className="font-medium">intermediario tecnológico</strong> que conecta a
            personas físicas o morales propietarias de vehículos ("Mobilitiers") con personas que desean rentarlos ("Renters" o
            "Usuarios"). Mobility <strong className="font-medium">no es propietario, arrendador, arrendatario, transportista
            ni operador de los vehículos</strong> publicados.
          </p>
          <p>
            Mobility no es parte del contrato de arrendamiento celebrado entre el Mobilitier y el Renter, ni garantiza la calidad,
            seguridad, legalidad o disponibilidad de los vehículos. Mobility actúa como un marketplace neutral y no media en
            disputas entre las partes (ver Sección 12).
          </p>
        </Section>

        <Section n="2" title="Aceptación y modificaciones">
          <p>
            Al crear una cuenta declaras tener al menos 18 años, contar con licencia de conducir vigente expedida en México o
            país compatible (cuando apliques como Renter) y capacidad legal para celebrar contratos.
          </p>
          <p>
            Mobility podrá modificar estos Términos en cualquier momento. Las modificaciones entrarán en vigor al ser publicadas
            en la Plataforma. El uso continuado constituye aceptación de las nuevas versiones.
          </p>
        </Section>

        <Section n="3" title="Registro y verificación de identidad">
          <p>
            Para utilizar la Plataforma debes registrarte con datos verídicos, completos y vigentes (nombre, correo, teléfono,
            licencia y, en su caso, INE). Mobility se reserva el derecho de validar la información con base en la
            <em> Ley General de Sociedades Mercantiles</em>, la
            <em> Ley Federal para la Prevención e Identificación de Operaciones con Recursos de Procedencia Ilícita</em> y demás
            normatividad aplicable.
          </p>
          <p>Eres el único responsable de mantener la confidencialidad de tu cuenta y contraseña.</p>
        </Section>

        <Section n="4" title="Obligaciones del Renter">
          <ul className="list-disc pl-6 space-y-2">
            <li>Conducir el vehículo con licencia vigente, dentro de la normatividad de tránsito y respetando los límites de uso pactados con el Mobilitier.</li>
            <li>Realizar el check-in (recepción) y check-out (devolución) conforme al protocolo de la Plataforma, incluyendo fotografías obligatorias del estado del vehículo.</li>
            <li>Devolver el vehículo en las mismas condiciones físicas y mecánicas en que fue recibido, salvo desgaste razonable por uso normal.</li>
            <li>Pagar oportunamente la renta, depósito de seguridad, casetas, multas, gasolina, daños y cualquier cargo aplicable.</li>
            <li>No subarrendar, no participar en carreras o actividades ilícitas, ni transportar mercancías peligrosas o ilegales.</li>
          </ul>
        </Section>

        <Section n="5" title="Obligaciones del Mobilitier">
          <ul className="list-disc pl-6 space-y-2">
            <li>Ser el legítimo propietario o tener autorización del propietario para rentar el vehículo.</li>
            <li>Mantener vigentes la verificación, tenencia, placas y póliza de seguro de cobertura amplia.</li>
            <li>Entregar el vehículo en condiciones mecánicas seguras, limpio y con la documentación al corriente.</li>
            <li>Cumplir con sus obligaciones fiscales (RFC, CFDI). Mobility entregará reportes de movimientos para facilitar el cumplimiento.</li>
            <li>Aceptar las cargas y políticas de comisión publicadas en la Plataforma.</li>
          </ul>
        </Section>

        <Section n="6" title="Pagos, depósito de seguridad y comisiones">
          <p>
            Los pagos se procesan a través de <strong className="font-medium">Stripe Connect</strong>. Mobility cobra una
            comisión por servicio del <strong className="font-medium">20%</strong> sobre el monto de la renta, salvo descuentos
            o promociones específicos (incluyendo el Programa de Referidos para Mobilitiers, que reduce la comisión a 15%
            durante 90 días al cumplirse las condiciones publicadas).
          </p>
          <p>
            Al confirmar una reserva se autorizan dos cargos en la tarjeta del Renter: (i) el monto de la renta, en estado
            "pendiente de captura" hasta la aprobación del Mobilitier; y (ii) un <strong className="font-medium">depósito
            de seguridad</strong> de hasta $750 MXN, retenido por la Plataforma y liberado automáticamente al finalizar el
            check-out sin daños reportados.
          </p>
          <p>
            En caso de cancelación por el Renter, las políticas de reembolso son: 100% si se cancela con más de 48 horas de
            anticipación; 50% entre 24 y 48 horas; 0% con menos de 24 horas. El Mobilitier puede rechazar reservas pendientes
            sin costo para el Renter (se libera la autorización del cargo).
          </p>
        </Section>

        <Section n="7" title="Multas, infracciones, casetas y combustible">
          <p>
            Toda multa, infracción de tránsito, costo de caseta o combustible faltante incurrido durante la renta es responsabilidad
            del Renter. Mobility puede cobrar dichos importes posteriormente al método de pago registrado, previa notificación.
          </p>
        </Section>

        <Section n="8" title="Daños y siniestros">
          <p>
            Los vehículos publicados deben contar con seguro de cobertura amplia vigente provisto por el Mobilitier. En caso de
            siniestro, robo o daño, Renter y Mobilitier deberán seguir el protocolo de la aseguradora correspondiente. Mobility
            colabora únicamente facilitando información de la reserva (fotografías de check-in/out, identificación de las partes).
          </p>
          <p>
            Si en el check-out se reporta un daño nuevo, se activa el flujo de "disputa pendiente" descrito en la Sección 12.
          </p>
        </Section>

        <Section n="9" title="Programa de Referidos">
          <p>
            <strong className="font-medium">Renters (B2C):</strong> al registrarte con un código válido recibes $200 MXN en
            créditos no canjeables por efectivo, aplicables a tu primera renta. El referidor recibe $100 MXN en créditos.
          </p>
          <p>
            <strong className="font-medium">Mobilitiers (B2B):</strong> al referir a otro Mobilitier que complete su primera
            renta, obtienes una comisión reducida del 15% (en lugar de 20%) durante 90 días desde la activación del bono.
            Los créditos y descuentos son intransferibles, no canjeables por dinero, y caducan según las reglas vigentes
            publicadas en la Plataforma.
          </p>
        </Section>

        <Section n="10" title="Conducta y suspensión">
          <p>
            Mobility puede suspender o eliminar cuentas que violen estos Términos, las leyes aplicables, los derechos de
            terceros, o que reciban reportes reiterados de mala conducta. La suspensión no genera derecho a indemnización.
          </p>
        </Section>

        <Section n="11" title="Propiedad intelectual">
          <p>
            La marca "Mobility", el logotipo, el código fuente, el diseño y los materiales de la Plataforma son propiedad
            exclusiva de Mobility Car Rental, S.A. de C.V., y están protegidos por las leyes mexicanas y tratados internacionales.
            Queda prohibida su reproducción sin autorización por escrito.
          </p>
        </Section>

        <Section n="12" title="Marketplace neutral · Resolución de disputas">
          <p>
            Mobility opera como marketplace neutral y <strong className="font-medium">no arbitra disputas entre Renter y
            Mobilitier</strong>. Si en el check-out se reporta un daño, el depósito de seguridad permanecerá retenido por
            Stripe hasta por el plazo máximo permitido por la red de pagos (aprox. 7 días). Transcurrido dicho plazo, el cargo
            se libera automáticamente. Las partes deberán resolver la controversia directamente, ante su aseguradora, ante el
            Profeco o, en su caso, ante los tribunales competentes.
          </p>
          <p>
            Los Términos se rigen por las leyes de los Estados Unidos Mexicanos. Para cualquier controversia, las partes se
            someten a los tribunales de la Ciudad de México, renunciando a cualquier otro fuero que pudiere corresponderles.
          </p>
        </Section>

        <Section n="13" title="Limitación de responsabilidad">
          <p>
            Mobility no es responsable por: (i) actos u omisiones del Mobilitier o del Renter; (ii) fallas mecánicas o eléctricas
            del vehículo; (iii) accidentes, lesiones o muerte; (iv) cargos no reportados oportunamente; (v) interrupciones del
            servicio digital por caso fortuito o fuerza mayor.
          </p>
        </Section>

        <Section n="14" title="Contacto">
          <p>Para cualquier duda sobre estos Términos puedes contactarnos en <a href="mailto:legal@mobility-car-rental.com" className="text-ink underline">legal@mobility-car-rental.com</a>.</p>
        </Section>

        <p className="mt-12 text-xs text-ink-400 font-light">
          Documento sujeto a revisión legal · No constituye asesoría jurídica · © Mobility Car Rental, S.A. de C.V.
        </p>
      </main>
    </div>
  );
};

export default Terms;
